import React from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Chip,
  keyframes,
  styled,
} from "@mui/material";
import { colors, primaryGradient } from "../../../theme/colors";
import { CollapseWrapper } from "./style.js";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";

const waveAnim = keyframes`
  0%, 100% { height: 6px; }
  50% { height: 22px; }
`;

const pulseDot = keyframes`
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
`;

const EqualizerBar = styled(Box)(({ delay }) => ({
  width: "4px",
  height: "14px",
  backgroundColor: colors.primary,
  borderRadius: "2px",
  animation: `${waveAnim} 1s ease-in-out infinite`,
  animationDelay: delay,
}));

const ChatSection = React.memo((props) => {
  const {
    transcript,
    handleGoToNextQuestion,
    currentAnswer,
    interviewQuestions,
    handleSubmitInterview,
    btnEnable,
    setBtnEnable,
    isProcessingQuestion,
    pendingApiCalls,
    isTransitioning,
  } = props;

  const totalQuestions = interviewQuestions?.length || 1;
  const questionNumber = Math.min(currentAnswer + 1, totalQuestions);
  const progressPercent = Math.min((questionNumber / totalQuestions) * 100, 100);

  const isApiProcessing =
    isProcessingQuestion ||
    (pendingApiCalls && pendingApiCalls.size > 0) ||
    isTransitioning;

  const isLastQuestion = currentAnswer === totalQuestions - 1;

  const isButtonDisabled =
    currentAnswer === totalQuestions ||
    !btnEnable ||
    isApiProcessing;

  const wordCount = transcript
    ? transcript.trim().split(/\s+/).filter(Boolean).length
    : 0;

  const getButtonText = () => {
    if (isApiProcessing) {
      return "Processing...";
    }
    return isLastQuestion ? "Submit Interview" : "Next Question";
  };

  const handleButtonClick = () => {
    if (isApiProcessing) {
      console.log("Button clicked while processing - ignoring");
      return;
    }

    setBtnEnable(false);

    if (isLastQuestion) {
      handleSubmitInterview();
    } else {
      handleGoToNextQuestion();
    }
  };

  return (
    <CollapseWrapper orientation="horizontal">
      <Box
        className="answer__container--card"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 0.5,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Chip
              icon={<RecordVoiceOverIcon sx={{ fontSize: "16px !important" }} />}
              label={`Question ${questionNumber} of ${totalQuestions}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                backgroundColor: colors.primaryLight,
                color: colors.primary,
                border: `1px solid ${colors.primaryLightBorder}`,
                borderRadius: "20px",
                px: 0.5,
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                height: "24px",
                px: 1.5,
                py: 0.5,
                borderRadius: "16px",
                backgroundColor: "#f9fafb",
                border: `1px solid ${colors.border.light}`,
              }}
            >
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#22c55e",
                  animation: `${pulseDot} 1.5s infinite`,
                  mr: 0.5,
                }}
              />
              <EqualizerBar delay="0s" />
              <EqualizerBar delay="0.15s" />
              <EqualizerBar delay="0.3s" />
              <EqualizerBar delay="0.45s" />
              <EqualizerBar delay="0.6s" />
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "#f3f4f6",
              "& .MuiLinearProgress-bar": {
                background: primaryGradient,
                borderRadius: 3,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            borderRadius: "14px",
            border: `1.5px solid ${colors.border.light}`,
            backgroundColor: "#ffffff",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 6px 24px rgba(0, 0, 0, 0.06)",
              borderColor: colors.primaryLightBorder,
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: `linear-gradient(90deg, ${colors.primaryLight} 0%, #ffffff 100%)`,
              borderBottom: `1px solid ${colors.border.light}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: colors.primary,
                letterSpacing: "0.3px",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MicIcon sx={{ fontSize: 18 }} />
              Your Live Answer
            </Typography>

            <Chip
              label={`${wordCount} words`}
              size="small"
              sx={{
                height: "22px",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: "#ffffff",
                color: colors.text.muted,
                border: `1px solid ${colors.border.light}`,
              }}
            />
          </Box>

          <Box
            className="answer__transcription--typography"
            sx={{
              p: 2.5,
              flexGrow: 1,
              overflowY: "auto",
              fontSize: "15px",
              lineHeight: 1.7,
              color: colors.text.primary,
              fontWeight: 500,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {transcript ? (
              <Typography
                component="div"
                sx={{
                  fontSize: "15px",
                  lineHeight: 1.7,
                  color: colors.text.primary,
                  fontWeight: 500,
                  whiteSpace: "pre-wrap",
                }}
              >
                {transcript}
              </Typography>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  minHeight: "180px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.text.muted,
                  textAlign: "center",
                  py: 4,
                }}
              >
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "50%",
                    backgroundColor: colors.primaryLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                    color: colors.primary,
                    border: `1px solid ${colors.primaryLightBorder}`,
                  }}
                >
                  <MicIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: colors.text.primary, mb: 0.5 }}
                >
                  Listening to your response...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.muted, maxWidth: "280px" }}
                >
                  Speak clearly into your microphone. Your answer will be transcribed here automatically in real time.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          className="btn__group"
          sx={{
            pt: 2,
            pb: 0.5,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            className="btn__next"
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            startIcon={
              isApiProcessing ? (
                <CircularProgress
                  size={18}
                  sx={{
                    color: "inherit",
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
              ) : (
                <SendIcon sx={{ fontSize: "18px !important" }} />
              )
            }
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "14px",
              textTransform: "none",
              background: isButtonDisabled
                ? colors.buttonDisabled.background
                : primaryGradient,
              color: isButtonDisabled
                ? colors.buttonDisabled.color
                : colors.white,
              boxShadow: isButtonDisabled
                ? "none"
                : "0 4px 14px rgba(127, 29, 29, 0.25)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: isButtonDisabled
                  ? colors.buttonDisabled.background
                  : `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primaryHover} 100%)`,
                boxShadow: isButtonDisabled
                  ? "none"
                  : "0 6px 20px rgba(127, 29, 29, 0.35)",
                transform: isButtonDisabled ? "none" : "translateY(-1px)",
              },
              "&.Mui-disabled": {
                backgroundColor: colors.buttonDisabled.background,
                color: colors.buttonDisabled.color,
                opacity: 1,
              },
            }}
          >
            {getButtonText()}
          </Button>
        </Box>
      </Box>
    </CollapseWrapper>
  );
});

export default ChatSection;
